<template>
  <div class="news-container">
    <!-- 顶部banner -->
    <div class="news-banner">
      <img src="@/assets/topbanner.jpg" alt="新闻资讯" class="banner-img">
      <div class="banner-text">新闻资讯</div>
    </div>

    <!-- 主体内容 -->
    <div class="news-content">
      <!-- 标签页切换 -->
      <div class="tabs">
        <div 
          :class="['tab', { active: activeTab === 'all' }]"
          @click="activeTab = 'all'; currentPage = 1"
        >
          全部新闻
        </div>
        <div 
          :class="['tab', { active: activeTab === 'company' }]"
          @click="activeTab = 'company'; currentPage = 1"
        >
          公司新闻
        </div>
        <div 
          :class="['tab', { active: activeTab === 'industry' }]"
          @click="activeTab = 'industry'; currentPage = 1"
        >
          行业动态
        </div>
        <div 
          :class="['tab', { active: activeTab === 'project' }]"
          @click="activeTab = 'project'; currentPage = 1"
        >
          项目进展
        </div>
        <div 
          :class="['tab', { active: activeTab === 'tech' }]"
          @click="activeTab = 'tech'; currentPage = 1"
        >
          技术创新
        </div>
      </div>

      <!-- 新闻列表 -->
      <div class="news-list">
        <div 
          v-for="news in currentNewsList" 
          :key="news._id"
          class="news-item"
          @click="handleChangePage(news._id)"
        >
          <div class="news-image">
            <img :src="news.cover" :alt="news.title" class="news-img">
          </div>
          <div class="news-info">
            <h3 class="news-title">{{ news.title }}</h3>
            <p class="news-summary">{{ news.summary || '暂无摘要信息...' }}</p>
            <div class="news-meta">
              <span class="news-date">{{ formatTime(news.editTime) }}</span>
              <span class="news-category">{{ getCategoryLabel(news.category) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <button 
          :disabled="currentPage === 1"
          @click="currentPage--"
          class="page-btn"
        >
          上一页
        </button>
        <span class="page-info">第 {{ currentPage }} 页</span>
        <button 
          :disabled="currentPage === totalPages"
          @click="currentPage++"
          class="page-btn"
        >
          下一页
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import moment from 'moment'
import { useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'

// SEO Meta标签配置
useHead({
  title: '新闻资讯 - 华洲电力建设有限公司',
  meta: [
    {
      name: 'description',
      content: '华洲电力建设有限公司最新新闻资讯，包括公司新闻、行业动态、项目进展和技术创新等内容。'
    },
    {
      name: 'keywords',
      content: '华洲电力,新闻资讯,公司新闻,行业动态,项目进展,技术创新'
    },
    {
      name: 'author',
      content: '华洲电力建设有限公司'
    },
    {
      property: 'og:title',
      content: '新闻资讯 - 华洲电力建设有限公司'
    },
    {
      property: 'og:description',
      content: '华洲电力建设有限公司最新新闻资讯，包括公司新闻、行业动态、项目进展和技术创新等内容。'
    },
    {
      property: 'og:type',
      content: 'website'
    }
  ]
})

const router = useRouter()
const newsList = ref([])
moment.locale('zh-cn')

// 标签页状态
const activeTab = ref('all')
// 分页状态
const currentPage = ref(1)
const pageSize = 2

// 标签页映射
const tabMap = {
  all: '全部新闻',
  company: '公司新闻',
  industry: '行业动态',
  project: '项目进展',
  tech: '技术创新'
}

// 分类映射
const categoryMap = {
  1: '公司新闻',
  2: '行业动态',
  3: '项目进展',
  4: '技术创新'
}

// 获取分类标签
const getCategoryLabel = (category) => {
  return categoryMap[category] || '其他'
}

// 计算当前显示的新闻列表
const currentNewsList = computed(() => {
  let filteredList = newsList.value
  
  // 根据选中的标签页过滤
  if (activeTab.value !== 'all') {
    const categoryMap = {
      company: 1,
      industry: 2,
      project: 3,
      tech: 4
    }
    const category = categoryMap[activeTab.value]
    filteredList = filteredList.filter(news => news.category === category)
  }
  
  // 分页
  const start = (currentPage.value - 1) * pageSize
  const end = start + pageSize
  return filteredList.slice(start, end)
})

// 计算总页数
const totalPages = computed(() => {
  let filteredList = newsList.value
  
  // 根据选中的标签页过滤
  if (activeTab.value !== 'all') {
    const categoryMap = {
      company: 1,
      industry: 2,
      project: 3,
      tech: 4
    }
    const category = categoryMap[activeTab.value]
    filteredList = filteredList.filter(news => news.category === category)
  }
  
  return Math.ceil(filteredList.length / pageSize)
})

// 格式化时间
const formatTime = (time) => {
  return moment(time).format('YYYY-MM-DD')
}

// 跳转到新闻详情页
const handleChangePage = (id) => {
  router.push({ path: `/news/${id}` })
}

// 获取新闻数据
const fetchNews = async () => {
  // try {
  //   const res = await axios.get('/webapi/news/list')
  //   console.log('新闻数据:', res.data.data)
  //   newsList.value = res.data.data
  // } catch (error) {
  //   console.error('获取新闻列表失败:', error)
    // 如果API调用失败，使用模拟数据
    newsList.value = [
      {
        _id: '1',
        title: '华洲电力联普我国电力行业数字化转型发展趋势分析',
        summary: '近日，随着数字技术的快速发展，电力行业正加速向数字化、智能化转型。本文从技术应用的技术蓬勃发展，科技壁垒一次次的被打破，现在的发展正以更快的速度进行发展...',
        cover: require('@/assets/dating.jpg'),
        editTime: new Date('2025-08-08'),
        category: 1
      },
      {
        _id: '2',
        title: '华洲电力成功中标大型电网建设项目',
        summary: '华洲电力建设有限公司近日成功中标某省大型电网建设项目，该项目总投资达5亿元，预计将在2023年底前完成建设。这标志着公司在电网建设领域的综合实力得到了市场的充分认可...',
        cover: require('@/assets/huikeshi.jpg'),
        editTime: new Date('2025-06-08'),
        category: 2
      },
      {
        _id: '3',
        title: '公司召开2023年安全生产工作会议',
        summary: '为进一步加强公司安全生产管理，提升全员安全意识，公司于近日召开了2023年安全生产工作会议。会议总结了过去一年的安全生产工作，并对新一年的安全生产任务进行了部署...',
        cover: require('@/assets/dating.jpg'),
        editTime: new Date('2023-08-08'),
        category: 1
      },
      {
        _id: '4',
        title: '电力行业绿色低碳发展论坛在京举行',
        summary: '由中国电力企业联合会主办的"电力行业绿色低碳发展论坛"近日在北京举行。来自全国各地的电力企业代表、专家学者齐聚一堂，共同探讨电力行业绿色低碳发展的路径和前景...',
        cover: require('@/assets/huikeshi.jpg'),
        editTime: new Date(Date.now() - 86400000 * 7),
        category: 2
      }
    ]
  // }
}

// 组件挂载时获取数据
onMounted(() => {
  fetchNews()
})
</script>

<style lang="scss" scoped>
.news-container {
  width: 100%;
  min-height: 100vh;
}

/* 顶部banner样式 */
.news-banner {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
}

.banner-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 36px;
  font-weight: bold;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

/* 主体内容样式 */
.news-content {
  width: 1200px;
  margin: 0 auto;
  padding: 40px 0;
}

/* 标签页样式 */
.tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
}

.tab {
  padding: 8px 24px;
  margin: 0 10px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  border-radius: 20px;
  transition: all 0.3s ease;
  background: #f0f0f0;
}

.tab.active {
  color: #fff;
  background: #3f5ff0;
  font-weight: 600;
}

.tab:hover:not(.active) {
  background: #a9b7f4;
  color: #3f5ff0;
}

/* 新闻列表样式 */
.news-list {
  margin-bottom: 40px;
}

.news-item {
  display: flex;
  padding: 20px;
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.news-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.news-image {
  flex: 0 0 200px;
  margin-right: 20px;
  overflow: hidden;
  border-radius: 4px;
}

.news-img {
  width: 100%;
  height: 140px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.news-item:hover .news-img {
  transform: scale(1.05);
}

.news-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.news-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.news-summary {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.news-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.news-date {
  font-size: 13px;
  color: #999;
}

.news-category {
  font-size: 13px;
  color: #3f5ff0;
  padding: 2px 8px;
  background: #a9b7f4;
  border-radius: 4px;
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.page-btn {
  padding: 8px 20px;
  background: #3f5ff0;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #3f5ff0;
}

.page-btn:disabled {
  background: #d9d9d9;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .news-content {
    width: 90%;
  }
}

@media (max-width: 768px) {
  .news-banner {
    height: 200px;
  }
  
  .banner-text {
    font-size: 24px;
  }
  
  .news-item {
    flex-direction: column;
  }
  
  .news-image {
    flex: none;
    margin-right: 0;
    margin-bottom: 15px;
  }
  
  .news-img {
    height: 200px;
  }
  
  .tabs {
    flex-wrap: wrap;
  }
  
  .tab {
    padding: 8px 15px;
    font-size: 14px;
  }
}
</style>
